import type { EvolutionIndicationItem } from './evolutionWorkflowTypes';

/** Registro de execução de item do plano/orçamento vinculado à evolução. */
export type EvolutionPlanExecutionRecord = {
  name: string;
  plan_execution: true;
  treatment_plan_item_id: string;
  procedure_execution_id: string;
  executed_at: string;
  session_number: number;
  sessions_total: number;
  executor_professional_name?: string;
  referring_professional_name?: string;
  notes?: string;
};

export function isEvolutionPlanExecutionEntry(entry: any): entry is EvolutionPlanExecutionRecord {
  return Boolean(
    entry
      && (entry.plan_execution === true || entry.treatment_plan_item_id)
      && entry.procedure_execution_id,
  );
}

export function parseEvolutionPlanExecutions(raw: unknown): EvolutionPlanExecutionRecord[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isEvolutionPlanExecutionEntry).map((entry) => ({
    name: String(entry.name || '').trim(),
    plan_execution: true as const,
    treatment_plan_item_id: String(entry.treatment_plan_item_id),
    procedure_execution_id: String(entry.procedure_execution_id),
    executed_at: String(entry.executed_at || ''),
    session_number: Number(entry.session_number) || 1,
    sessions_total: Number(entry.sessions_total) || 1,
    executor_professional_name: entry.executor_professional_name || undefined,
    referring_professional_name: entry.referring_professional_name || undefined,
    notes: entry.notes || undefined,
  }));
}

export function splitEvolutionProceduresAndPlanExecutions(raw: unknown): {
  planExecutions: EvolutionPlanExecutionRecord[];
  indications: EvolutionIndicationItem[];
} {
  const planExecutions = parseEvolutionPlanExecutions(raw);
  const planKeys = new Set(planExecutions.map((p) => p.procedure_execution_id));
  const indications = (Array.isArray(raw) ? raw : [])
    .filter((entry) => !planKeys.has(String((entry as any)?.procedure_execution_id || '')))
    .map((entry: any) => {
      if (typeof entry === 'string') return entry.trim() ? { name: entry.trim() } : null;
      if (isEvolutionPlanExecutionEntry(entry)) return null;
      const name = String(entry?.name || entry?.label || '').trim();
      if (!name) return null;
      return {
        name,
        notes: entry?.notes || undefined,
        procedure_id: entry?.procedure_id ?? null,
        category_id: entry?.category_id ?? null,
        category_name: entry?.category_name ?? null,
        act_type: entry?.act_type ?? null,
        source: entry?.source ?? null,
      };
    })
    .filter(Boolean) as EvolutionIndicationItem[];
  return { planExecutions, indications };
}

function formatExecDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString('pt-BR');
}

export function formatEvolutionPlanExecutionSummaryLine(rec: EvolutionPlanExecutionRecord): string {
  const sess =
    rec.sessions_total > 1 ? ` (sessão ${rec.session_number}/${rec.sessions_total})` : '';
  const exec = rec.executor_professional_name ? ` · executor: ${rec.executor_professional_name}` : '';
  const ref = rec.referring_professional_name ? ` · indicador: ${rec.referring_professional_name}` : '';
  const note = rec.notes?.trim() ? ` · obs.: ${rec.notes.trim()}` : '';
  return `${rec.name}${sess} — executado em ${formatExecDate(rec.executed_at)}${exec}${ref}${note}`;
}

/** Parágrafo HTML para anexar ao texto livre da evolução. */
export function buildEvolutionPlanExecutionHtmlParagraph(rec: EvolutionPlanExecutionRecord): string {
  const line = formatEvolutionPlanExecutionSummaryLine(rec);
  return `<p><strong>Procedimento executado:</strong> ${escapeHtml(line)}</p>`;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function appendPlanExecutionToFreeText(currentHtml: string, rec: EvolutionPlanExecutionRecord): string {
  const block = buildEvolutionPlanExecutionHtmlParagraph(rec);
  const trimmed = String(currentHtml || '').trim();
  return trimmed ? `${trimmed}${block}` : block;
}
